import { useTranslation } from "react-i18next";
import { useFormContext, Controller } from "react-hook-form";
import {
  Box,
  Radio,
  RadioGroup,
  Typography,
  FormControl,
  FormLabel,
  Divider,
  FormControlLabel,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  Slider,
  FormHelperText,
} from "@mui/material";

import { useCountryListing } from "../../../../../../api/queries/network/useCountryListing";
import { CreateServiceFormData } from "../../../../types/service.type";
import { entityRoleEnum, entityRoles } from "../../../../../../enums/entity.enum";

const CustomerPreferences = () => {
  const { t } = useTranslation("goService", { keyPrefix: "createServicePage.customerPreferencesStep" });
  const { t: tCommon } = useTranslation("common", { keyPrefix: "user.role" });

  const restrictServiceOptions = [
    {
      value: "true",
      label: t("yes"),
    },
    {
      value: "false",
      label: t("no"),
    },
  ];

  const { countries, isCountriesDataLoading } = useCountryListing();

  const countriesList =
    countries.map(({ label, value }: { label: string; value: string }) => ({
      label,
      value,
    })) ?? [];

  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<CreateServiceFormData>();

  const isRestricted = watch("customerPreferenceConfig.restrict");

  return (
    <Box sx={styles.container}>
      <Box>
        <Typography sx={styles.formLabel}>{t("preferredCustomers")}</Typography>
        <Typography sx={{ ...styles.formLabelDescription, maxWidth: { xs: "100%", sm: "512px" } }}>
          {t("selectingPreferred")}
        </Typography>
      </Box>
      <Controller
        name="customerPreferenceConfig.restrict"
        control={control}
        render={({ field }) => (
          <FormControl>
            <FormLabel sx={styles.formChildLabel}>{t("restrictService")}</FormLabel>
            <RadioGroup
              sx={styles.serviceDeliveryRadioGroup}
              value={field.value}
              onChange={(e) => {
                const value = e.target.value === "true";
                field.onChange(value);
              }}
            >
              {restrictServiceOptions.map(({ value, label }) => (
                <FormControlLabel key={String(value)} value={value} control={<Radio size="small" />} label={label} />
              ))}
            </RadioGroup>
          </FormControl>
        )}
      />

      <Divider />

      {isRestricted && (
        <>
          <Controller
            name="customerPreferenceConfig.personas"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors?.customerPreferenceConfig?.personas}>
                <FormLabel sx={styles.formChildLabel}>{t("preferredCustomer")}</FormLabel>
                <Select
                  displayEmpty
                  fullWidth
                  value={field.value}
                  onChange={(e) => {
                    const value = e.target.value as entityRoleEnum[];
                    field.onChange(value);
                  }}
                  multiple
                >
                  <MenuItem selected value="" disabled>
                    {t("selectCustomers")}
                  </MenuItem>
                  {entityRoles.map((role, idx) => (
                    <MenuItem key={idx} value={role}>
                      {tCommon(role)}
                    </MenuItem>
                  ))}
                </Select>
                {errors?.customerPreferenceConfig?.personas && (
                  <FormHelperText error>{t(errors.customerPreferenceConfig.personas.message)}</FormHelperText>
                )}
              </FormControl>
            )}
          />
          <Divider />
          <Controller
            name="customerPreferenceConfig.locations"
            control={control}
            render={({ field }) => {
              const handleOnChange = (_: any, newValue: { label: string; value: number }[]) => {
                field.onChange(newValue.map((item) => item.value));
              };

              return (
                <FormControl sx={styles.formControl} error={!!errors?.customerPreferenceConfig?.locations}>
                  <FormLabel sx={styles.formChildLabel}>{t("customerLocation")}</FormLabel>
                  <Autocomplete
                    multiple
                    disablePortal
                    disabled={isCountriesDataLoading}
                    options={countriesList}
                    value={countriesList.filter((country: { label: string; value: number }) =>
                      field.value?.includes(country.value)
                    )}
                    onChange={handleOnChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t("selectCustomerLocation")}
                        error={!!errors?.customerPreferenceConfig?.locations}
                      />
                    )}
                  />
                  {errors?.customerPreferenceConfig?.locations && (
                    <FormHelperText error>
                      {t(errors?.customerPreferenceConfig?.locations.message as string)}
                    </FormHelperText>
                  )}
                </FormControl>
              );
            }}
          />
          <Divider />
        </>
      )}
      <Controller
        name="customerPreferenceConfig.ageRange"
        control={control}
        render={({ field }) => (
          <FormControl sx={styles.formControl} error={!!errors?.customerPreferenceConfig?.ageRange}>
            <FormLabel sx={styles.formChildLabel}>{t("ageRange")}</FormLabel>
            <Slider
              value={[field.value?.minAge ?? 0, field.value?.maxAge ?? 100]}
              onChange={(event: Event, newValue: number[] | number) => {
                const values = newValue as number[];
                field.onChange({
                  minAge: values[0],
                  maxAge: values[1],
                });
              }}
              getAriaLabel={() => "Temperature range"}
              valueLabelDisplay="auto"
            />
            {errors?.customerPreferenceConfig?.ageRange && (
              <FormHelperText error>{t(errors.customerPreferenceConfig.ageRange.message)}</FormHelperText>
            )}
          </FormControl>
        )}
      />
    </Box>
  );
};

export default CustomerPreferences;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  serviceDeliveryRadioGroup: {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
  },
  formLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  formLabelDescription: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  formControlLabel: {
    margin: 0,
    gap: "12px",
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  formChildLabel: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  formControl: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
};
